import { useRequiredContext } from "@redotech/react-util/context";
import { GlobalAgentContext } from "@redotech/redo-merchant-app-common/global-agent-context";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { Message } from "@redotech/redo-model/agents/llm-messages";
import { AiChatProvider } from "@redotech/redo-web/ai/chat/contexts/ai-chat-context";
import { transformMessagesForBackend } from "@redotech/redo-web/ai/chat/utils/messages";
import * as React from "react";
import { memo, useCallback, useMemo, useRef, useState } from "react";
import { EscalateToHumanTool } from "../../help-chat/escalate-to-human-tool";
import { HelpChatGlobal } from "../../help-chat/help-chat-global";
import { CreateHyperLinkTool } from "./global-search-tools/create-hyper-link-tool";
import { UpdateIntegrationTool } from "./global-search-tools/update-integration-tool";

interface GlobalAIProps {
  children?: React.ReactNode;
}

export const GlobalAI = memo(function GlobalAI({ children }: GlobalAIProps) {
  const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const user = useRequiredContext(UserContext);
  const { pageContext } = React.useContext(GlobalAgentContext);

  // state is used to control the UI thread context while refs are used during sends
  const [_currentThreadId, setCurrentThreadId] = useState<string | undefined>();
  const [_triageThreadId, setTriageThreadId] = useState<string | undefined>();
  const triageThreadCallbackRef = useRef<((threadId: string) => void) | null>(
    null,
  );

  // Use refs to always have the most current values for the callback
  const currentThreadIdRef = useRef<string | undefined>();
  const triageThreadIdRef = useRef<string | undefined>();

  const toolComponents = useMemo(
    () => ({
      "update-integration": UpdateIntegrationTool,
      "create-hyper-link": CreateHyperLinkTool,
      "escalate-to-human": EscalateToHumanTool,
    }),
    [],
  );

  const handleToolResult = useCallback(async (args: any) => {
    if (args.toolName === "escalate-to-human") {
      // No command palette to close in AI-only mode
    }
  }, []);

  const handleChatOpen = useCallback(() => {
    setCurrentThreadId(undefined);
    setTriageThreadId(undefined);
    currentThreadIdRef.current = undefined;
    triageThreadIdRef.current = undefined;
  }, []);

  const handleLoadThread = useCallback((threadId: string, thread: unknown) => {
    // When loading from thread history, always continue with the triage agent
    // The triage agent will handle routing to child agents as needed
    // Continue with triage agent
    setCurrentThreadId(threadId);
    setTriageThreadId(threadId);
    currentThreadIdRef.current = threadId;
    triageThreadIdRef.current = threadId;
  }, []);

  const sendMessageToAI = useCallback(
    async ({ newMessages }: { newMessages: Message[] }) => {
      const transformedMessages = transformMessagesForBackend(newMessages);

      // Use refs to get the most current values
      const currentCurrentThreadId = currentThreadIdRef.current;
      const currentTriageThreadId = triageThreadIdRef.current;

      // Page context: if set (e.g., from Returns Ask AI), always include it
      // so the triage agent has consistent context on this page.

      const response = await merchantRpcClient.messageMerchantSupportAgent({
        messages: transformedMessages,
        currentThreadId: currentCurrentThreadId,
        triageThreadId: currentTriageThreadId,
        userEmail: user.email,
        // Always pass pageContext when available (not only for new threads)
        ...(pageContext ? { pageContext } : {}),
      });

      // Check if this created a new triage thread
      const wasNewTriageThread =
        !currentTriageThreadId && response.thread.triageThreadId;

      // Update both state and refs
      setCurrentThreadId(response.thread.currentThreadId);
      setTriageThreadId(response.thread.triageThreadId);
      currentThreadIdRef.current = response.thread.currentThreadId;
      triageThreadIdRef.current = response.thread.triageThreadId;

      // If a new triage thread was created, trigger thread refresh
      if (wasNewTriageThread) {
        triageThreadCallbackRef.current?.(response.thread.triageThreadId);
      }

      // Keep pageContext so it continues to be sent while on this page

      return { messages: response.thread.newMessages };
    },
    [merchantRpcClient, user.email, pageContext],
  );

  React.useEffect(() => {
    return () => {
      triageThreadCallbackRef.current = null;
    };
  }, []);

  return (
    <AiChatProvider
      additionalToolComponents={toolComponents}
      onToolResult={handleToolResult}
      sendMessageToAI={sendMessageToAI}
    >
      {children}
      <HelpChatGlobal
        onChatOpen={handleChatOpen}
        onLoadThread={(threadId: string, thread: unknown) =>
          handleLoadThread(threadId, thread)
        }
        onRegisterTriageThreadCallback={(
          callback: (threadId: string) => void,
        ) => {
          triageThreadCallbackRef.current = callback;
        }}
      />
    </AiChatProvider>
  );
});
